const content = `# Organizing workflows with folders

Folders give the workflow library a left-rail you can use to slice the table
by team, customer, demo theme, or anything you like.  This article covers
two paths: the **per-row kebab actions** (always available, keyboard-friendly)
and the **drag-and-drop** that landed in this release.

## The folders rail

On screens wider than 768 px the workflow library shows a left sidebar with:

- **All workflows** — the default view, no filter.
- **Unfoldered** — workflows that aren't in any folder.  This row doubles as
  a drop target: dragging a workflow here removes it from every folder it
  currently lives in.
- One row per folder you can see (your private folders + the org-shared ones).
  Each row shows the folder's color/icon and the count of workflows in it.

Click any row to filter the table.  The search input at the top filters the
folder list itself — useful when you have dozens of folders.

On 1024 px and below the rail collapses to an icon-only column with full
text-on-hover via tooltips.  Below 768 px the rail is hidden and you should
use the kebab-menu actions instead (described later in this article).

## Folder name chips in the table

The Folders column now renders inline name chips instead of a "📁 N" badge.
Each chip is small but readable, color-coded to the folder palette, and has
a tiny **×** that removes the workflow from that folder.  When a workflow
sits in more than two folders the extra entries collapse into a
"**+N more**" pill — hover it to read the full list, or click to open the
Add-to-folder menu.

Workflows in zero folders show a faint "—" with a small "+" affordance.
Click the affordance to attach the workflow to a folder without leaving the
library.

## Drag-and-drop

Each row has a vertical grip icon (⋮⋮) at its left edge.  Drag the grip onto
any folder in the sidebar to attach the workflow:

- **Default drop** = ADD.  The workflow is added to the target folder; any
  existing folder memberships are preserved.
- **Shift + drop** = MOVE.  The workflow is removed from every other folder
  and placed only in the target.  We confirm before doing this so it doesn't
  surprise anyone.

The drag handle is fully keyboard-accessible: focus it with Tab, press
**Enter** or **Space** to pick up the workflow, use the arrow keys to step
through drop targets, then press **Enter** again to commit.

If you have two or more rows checkbox-selected and grab any selected row's
handle, the drag operates on all selected workflows at once — the cursor
chip shows the count instead of a name.

## Keyboard-only fallback

The kebab menu on each row still has **Add to folder…** and **Move to
folder…** items — these work without a mouse and are the recommended path
on narrow screens or in screen-reader sessions.  The drag handle is purely
additive.

## Audit trail

Every membership change writes an audit event you can review at
\`/audit\`:

- \`folder_workflow_item_added\` — single ADD via drop, chip, or kebab.
- \`folder_workflow_item_removed\` — chip X or kebab "Remove from folder".
- \`folder_workflow_item_moved\` — Shift+drop or the kebab "Move to folder…"
  action.  Captures source folders + target so the trail tells the full
  story.

## Tips

- **Color-code by theme.** Folders can pick any of the 12 brand palettes —
  rose for "Customer demos", emerald for "Internal", indigo for "Templates",
  etc.
- **Org folders vs. mine.** Folders you create default to private.  Use the
  visibility toggle in the folder settings dialog to share with the org.
- **Folder counts update live.** When you drop into a folder the count in
  the sidebar reflects the change immediately — refresh isn't needed.
- **Drop on "Unfoldered" to detach.** Dragging onto the Unfoldered row is
  the fastest way to clear every folder from a workflow at once.
`;

export default content;
